import type { Monaco } from "@monaco-editor/react"
import { LAYER_EDITOR_SCHEMAS } from "@/v2-backoffice-client/layer/update/schema/layer.update.schema"
import { BASEMAP_EDITOR_SCHEMAS } from "@/v2-backoffice-client/basemap/update/basemap.update.schema"
import { NAV_LAYER_EDITOR_SCHEMAS } from "@/v2-backoffice-client/navlayer/update/navlayer.update.schema"
import { getCatalogEditorModelPath } from "@/v2-backoffice-client/feature/configuration/feature.configuration.constants"

/**
 * Monaco holds ONE schema list per language for the whole page, so the
 * catalogs cannot each register their own — the last call would replace the
 * others. Every catalog exports its schemas keyed by model name and this is
 * the single place they are handed over, the same way apollo.cache collects
 * each domain's field policies.
 */
const CATALOG_EDITOR_SCHEMAS = {
  ...LAYER_EDITOR_SCHEMAS,
  ...BASEMAP_EDITOR_SCHEMAS,
  ...NAV_LAYER_EDITOR_SCHEMAS,
}

let isRegistered = false

/**
 * Registers every catalog editor's JSON schema with Monaco's JSON language
 * service. Gives the editors autocomplete, hover docs, enum suggestions and
 * validation squiggles. Schema problems surface as WARNINGS and never block
 * saving — the JSON stays the source of truth.
 *
 * Pass as `beforeMount` to the editor; it self-guards so mounting several
 * editors only registers once.
 */
export function registerCatalogEditorSchemas(monaco: Monaco) {
  if (isRegistered) return
  isRegistered = true

  monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
    validate: true,
    enableSchemaRequest: false,
    // Deviations from the schema are advisory, never errors: the schemas are
    // curated by hand and must not fight legitimate exotic configs.
    schemaValidation: "warning",
    schemas: Object.entries(CATALOG_EDITOR_SCHEMAS).map(
      ([modelName, schema]) => {
        const uri = getCatalogEditorModelPath(modelName)
        return { uri, fileMatch: [uri], schema }
      },
    ),
  })
}
